import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Banner, Page, PageBody, PageLoader } from '@customerio/pluma-components/react';
import { DocumentTitle } from '@/components/page/document-title';
import { SafeNavigate } from '@/services/router/safe-navigate';
import { getIntegrationsQueryOptions } from '@/services/db/integration/queries';
import { ExternalIntegrationPanel } from './components/external-integration-panel';
import { integrationsOverviewHref, useExternalIntegrationBreadcrumbs } from './hooks';
import { crumbName, normalizeName } from './page-model';

const LOADING_LABEL = 'Loading integration…';

type ExternalIntegrationPageProps = {
  /** The catalog id from the URL. */
  type: string;
  workspaceId: string;
};

/**
 * The catch-all `/integrations/:type` page: the marketing detail for any
 * integration in the workspace's catalog.
 *
 * The catalog is workspace-scoped, so an id that resolves in one workspace can
 * be absent in another — a stale link, a bookmark, or a workspace switch. That
 * case goes back to the directory rather than rendering a page with no record
 * behind it (CDP-6066).
 */
export default function ExternalIntegrationPage({ type, workspaceId }: ExternalIntegrationPageProps) {
  const qc = useQueryClient();
  const catalog = useQuery(getIntegrationsQueryOptions(Number(workspaceId), qc));
  const integration = catalog.data?.find((candidate) => candidate.id === type);

  useExternalIntegrationBreadcrumbs(
    workspaceId,
    type,
    integration == null ? normalizeName(type) : crumbName(integration),
  );

  if (catalog.isPending) {
    return <PageLoader label={LOADING_LABEL} />;
  }

  // A failed read is not an absent integration: redirecting here would blame the
  // link for a request that simply did not land.
  if (catalog.isError) {
    return (
      <Page>
        <PageBody>
          <Banner
            variant="critical"
            title="We couldn't load this integration"
            description="Refresh the page to try again."
          />
        </PageBody>
      </Page>
    );
  }

  if (integration == null) {
    return <SafeNavigate to={integrationsOverviewHref(workspaceId)} options={{ shouldReplaceHistoryEntry: true }} />;
  }

  return (
    <>
      <DocumentTitle title={integration.name} />
      <Page>
        <PageBody>
          <ExternalIntegrationPanel integration={integration} workspaceId={workspaceId} />
        </PageBody>
      </Page>
    </>
  );
}
